// Production specs (ERP-PLAN.md §5.11), shared by the server and the screens: revision letters, what a release
// would change, the status shown everywhere, file kinds and the starting template. Mutations:
// shared/mutations-specs.ts; rules and releases: server/src/mutations/specs.ts.
import { hash64 } from "./hash.ts";

export const SPEC_FILE_KINDS = [
  "pcb_fab",
  "pcba",
  "kicad",
  "design",
  "firmware",
  "step",
  "drawing",
  "datasheet",
  "artwork",
  "dieline",
  "fixture",
  "other",
] as const;
export type SpecFileKind = (typeof SPEC_FILE_KINDS)[number];
export const SPEC_FILE_KIND_LABELS: Record<SpecFileKind, string> = {
  pcb_fab: "PCB fab (Gerbers)",
  pcba: "PCBA (BOM + placement)",
  // A board's KiCad project (migration 053): HHL Ops makes its files from it and reads its components (the Board tab).
  kicad: "KiCad project",
  design: "PCB design files",
  firmware: "Firmware",
  step: "3D (STEP)",
  drawing: "2D drawing",
  // The maker's datasheet (migration 068; Mitch, 2026-11-03: "datasheet should be a valid type").
  datasheet: "Datasheet",
  artwork: "Artwork / print",
  dieline: "Packaging dieline",
  fixture: "Test fixture",
  other: "Other",
};

// PCB export files by name (KiCad, Altium/Protel, EAGLE, EasyEDA): the layers, drill and report files a board's
// Gerbers zip is made of, and its placement and BOM files. The file drop (shared/spec-drop.ts) zips a set of them.
// EasyEDA's "How-to-order-PCB.txt" and KiCad's IPC-D-356 netlist (.d356) ride along too; EAGLE's .dim is the outline.
const PCB_EXTRA =
  /\.(gbrjob|apr|apr_lib|rep|drr|ldp|extrep|rul|gpi|dri|ipc|d356|rpt)$|^status report.*\.txt$|^how[ _-]to[ _-]order.*\.txt$|layer[ _-]?order.*\.txt$|下单必读|drl_map|drill[ _-]?map/;
const PCB_LAYER =
  /\.(gbr|ger|gbx|pho|art|gtl|gbl|gto|gbo|gts|gbs|gtp|gbp|gko|gml|gm\d{1,2}|g\d{1,2}|gp\d{1,2}|gd\d{1,2}|gg\d{1,2}|gpt|gpb|gdd|gdl|cmp|sol|stc|sts|plc|pls|crc|crs|dim)$/;
const PCB_DRILL = /\.(drl|xln|exc|drd|nc|ncd|tap)$|(round|slot)[ _-]?holes.*\.txt$/;
const DATA = /\.(csv|txt|tsv|xlsx?|ods)$/;
const PLACEMENT = /\.(pos|mnt|mnb)$/;
/** KiCad's "-top-pos.csv", also with a date, revision or a browser's " (1)" after the "pos" ("-top-pos_R5 (1).csv"). */
const PLACEMENT_CSV = /[-_ ]pos$/;
const PLACEMENT_WORDS = /(^|[^a-z])(pick|place|position|centroid)|(^|[^a-z0-9])(pnp|cpl|xy)([^a-z0-9]|$)/;
const BOM_WORDS = /(^|[^a-z0-9])bom|bill[ _-]?of[ _-]?materials/;

export type PcbFileRole = "layer" | "drill" | "extra" | "placement" | "bom";

/**
 * What a PCB export file is from its name: a Gerber layer, a drill file, a report that rides along in the Gerbers zip
 * (job file, aperture list, drill map…), a placement (pick and place) file or a BOM. Null for anything else.
 */
export function pcbFileRole(filename: string): PcbFileRole | null {
  const f = (filename.split(/[\\/]/).pop() ?? "").toLowerCase();
  if (PCB_EXTRA.test(f)) return "extra";
  if (PCB_LAYER.test(f)) return "layer";
  if (PCB_DRILL.test(f)) return "drill";
  const stem = withoutDateRev(f.replace(/\.[a-z0-9]{1,5}$/, "").replace(/\s*\(\d{1,3}\)$/, ""));
  if (PLACEMENT.test(f) || (f.endsWith(".csv") && PLACEMENT_CSV.test(stem)) || (DATA.test(f) && PLACEMENT_WORDS.test(f))) return "placement";
  if (DATA.test(f) && BOM_WORDS.test(f)) return "bom";
  return null;
}

/**
 * A first guess at a file's kind from its name (people can change it). A zip is a board package only on a page
 * about a board (`context`, the page title); a molded part's zip of STEP files isn't Gerbers.
 */
export function guessFileKind(filename: string, context = ""): SpecFileKind {
  const f = filename.toLowerCase();
  if (/kicad project\.zip$/.test(f)) return "kicad";
  if (/design files\.zip$|\.(kicad_pcb|kicad_sch|kicad_pro|pcbdoc|schdoc|prjpcb|brd|epro|eprj)$/.test(f)) return "design";
  if (/dieline|die[ -]?cut/.test(f)) return "dieline";
  const pcb = pcbFileRole(f);
  if (pcb) return pcb === "placement" || pcb === "bom" ? "pcba" : "pcb_fab";
  if (/gerber|\.gbr$|\.gtl$|_fab|pcb.*\.zip$/.test(f)) return "pcb_fab";
  if (/pick|place|\bpnp\b|\bcpl\b|centroid|bom.*\.(csv|xlsx?)$|pcba/.test(f)) return "pcba";
  if (/\.(bin|uf2|hex|elf|dfu)$|firmware|\.ino\b|(fcc|test).*\.zip$/.test(f)) return "firmware";
  if (/\.(step|stp|iges|igs|stl|x_t|sldprt|f3d)$/.test(f)) return "step";
  if (/fixture|jig|tester/.test(f)) return "fixture";
  if (/\.(ai|eps|psd|svg|indd)$|artwork|print|sticker/.test(f)) return "artwork";
  if (/data ?sheet|spec ?sheet/.test(f)) return "datasheet";
  if (/manual/.test(f)) return "other";
  if (/\.(pdf|dxf|dwg)$|drawing/.test(f)) return "drawing";
  if (/\.zip$/.test(f) && /pcb|board|fpc|flex|dongle|(^|[^a-z])main([^a-z]|$)/i.test(`${f} ${context}`)) return "pcb_fab";
  return "other";
}

/** A file slot's name: no extension, no trailing dates or revisions ("Main Board 9.21.2026r1.zip" → "Main Board"). */
export function fileSlotName(filename: string): string {
  const base = filename.replace(/\.[a-z0-9]{1,5}$/i, "");
  return withoutDateRev(base).replace(/_+/g, " ").trim() || base || filename;
}

/** A name without its trailing dates and revisions ("Main_R5 10.25.2026" → "Main"); what was cut is the rest of it. */
export function withoutDateRev(name: string): string {
  let cleaned = name;
  for (let before = ""; before !== cleaned; ) {
    before = cleaned;
    cleaned = cleaned
      .replace(/[\s_-]*\d{4}[./-]\d{1,2}[./-]\d{1,2}$/, "")
      .replace(/[\s_-]*\d{1,2}[./-]\d{1,2}[./-]\d{2,4}$/, "")
      .replace(/[\s_-]*(r|rev|v)\.?\s?\d+[a-z]?$/i, "")
      .trim();
  }
  return cleaned;
}

// Revisions are numbered 1, 2, 3… (migration 058; Mitch, 2026-11-02: "Numerical"): one number per item, a sub-assembly's
// spec and BOM sharing it (server/src/item-release.ts).
export const revNumber = (rev: string) => Number(rev) || 0;
/** The number after the highest of `revs` (1 for none). */
export const nextRev = (revs: Iterable<string>) => String(Math.max(0, ...[...revs].map(revNumber)) + 1);
/** Sorts revisions oldest first (2 before 10). */
export const byRev = (a: { rev: string }, b: { rev: string }) => revNumber(a.rev) - revNumber(b.rev);

/**
 * Each item's spec: its own, or for a Shopify variant its product's (migration 059: one spec per product; the SQL is
 * `item_spec()`). `items` are only read for variants, so pass them all.
 */
export function specsByItem<S extends { item_id: string | null; shopify_product_id: string | null }>(
  specs: readonly S[],
  items: Iterable<{ id: string; source: string; shopify_product_id: string | null }>,
): Map<string, S> {
  const out = new Map<string, S>();
  const byProduct = new Map<string, S>();
  for (const s of specs) {
    if (s.item_id) out.set(s.item_id, s);
    else if (s.shopify_product_id) byProduct.set(s.shopify_product_id, s);
  }
  if (byProduct.size)
    for (const i of items) {
      const s = i.source === "shopify" && i.shopify_product_id ? byProduct.get(i.shopify_product_id) : undefined;
      if (s && !out.has(i.id)) out.set(i.id, s);
    }
  return out;
}

/**
 * One file version in a revision or the draft: its slot, its checksum, and the label and place it's shown with. A
 * released revision keeps its own copy of the label (spec_revision_file), so renaming a slot later never changes it.
 */
export type FileRef = { spec_file_id: string; file_version_id: string; sha256: string; name: string; kind: SpecFileKind; position: number };

export type FileChanges = { added: FileRef[]; removed: FileRef[]; changed: { from: FileRef; to: FileRef }[]; same: FileRef[] };

/** Same bytes: the same version, or another version with the same checksum (a file uploaded again unchanged). */
const sameBytes = (a: FileRef, b: FileRef) => a.file_version_id === b.file_version_id || (!!a.sha256 && a.sha256 === b.sha256);

/** What happened to the files between two revisions (or a revision and the draft), slot by slot. */
export function fileChanges(from: FileRef[], to: FileRef[]): FileChanges {
  const before = new Map(from.map((f) => [f.spec_file_id, f]));
  const after = new Map(to.map((f) => [f.spec_file_id, f]));
  const out: FileChanges = { added: [], removed: [], changed: [], same: [] };
  for (const [slot, f] of after) {
    const old = before.get(slot);
    if (!old) out.added.push(f);
    else if (sameBytes(old, f)) out.same.push(f);
    else out.changed.push({ from: old, to: f });
  }
  for (const [slot, f] of before) if (!after.has(slot)) out.removed.push(f);
  return out;
}

type DraftFile = { id: string; name: string; kind: SpecFileKind; position: number; in_draft: boolean; current_version_id: string | null };

/** The files the next release would freeze: each in-draft file's newest upload (`sha256` looks up a version's checksum). */
export const draftFiles = (files: DraftFile[], sha256: (versionId: string) => string | undefined): FileRef[] =>
  files
    .filter((f) => f.in_draft && f.current_version_id)
    .map((f) => ({
      spec_file_id: f.id,
      file_version_id: f.current_version_id as string,
      sha256: sha256(f.current_version_id as string) ?? "",
      name: f.name,
      kind: f.kind,
      position: f.position,
    }));

export type Released = { body_md: string; body_md_zh: string; files: FileRef[] };

/** What releasing the draft (its text and `draft` files) would change against the current revision (or, never released, what it would release). */
export function pendingRelease(spec: { draft_md: string; draft_md_zh: string }, draft: FileRef[], current: Released | null) {
  const fc = fileChanges(current?.files ?? [], draft);
  const text = spec.draft_md !== (current?.body_md ?? "");
  const zh = spec.draft_md_zh !== (current?.body_md_zh ?? "");
  const filesChanged = fc.added.length + fc.removed.length + fc.changed.length > 0;
  return { text, zh, files: fc, any: text || zh || filesChanged, empty: !spec.draft_md.trim() && !spec.draft_md_zh.trim() && !draft.length };
}

/**
 * A fingerprint of exactly what releasing the draft would freeze: the text, the 中文 text and each in-draft file's
 * version, label and place. A release dialog sends the one it showed; the server refuses if the draft has moved on
 * since (someone uploaded, or an MCP draft edit landed), so an admin never releases what they didn't review.
 */
export function draftStamp(spec: { draft_md: string; draft_md_zh: string }, draft: FileRef[]): string {
  const files = draft.map((f) => JSON.stringify([f.file_version_id, f.kind, f.name, f.position])).sort();
  return hash64(JSON.stringify([spec.draft_md, spec.draft_md_zh, files]));
}

/** Shown when a release's stamp no longer matches the draft. */
export const DRAFT_MOVED = "The draft changed since you opened this. Review it again";

export type SpecStatus = { kind: "none" } | { kind: "draft" } | { kind: "released"; rev: string } | { kind: "ahead"; rev: string };

export const specStatusLabel = (s: SpecStatus) =>
  s.kind === "none" ? "No spec" : s.kind === "draft" ? "Draft" : s.kind === "released" ? `Rev ${s.rev}` : `Draft ahead of Rev ${s.rev}`;

export const SPEC_STATUS_TONE = { none: "neutral", draft: "accent", released: "ok", ahead: "warn" } as const;

/** Where a new spec starts (ERP-PLAN.md §5.11). */
export const SPEC_TEMPLATE = `## Overview


## Specifications

| Specification | Value |
|---|---|
|  |  |

## Tolerances and critical dimensions


## Materials and finish


## Assembly and handling


## Inspection and test


## Packaging and labelling


## Warnings

`;
